import { useState } from 'react';
import { ChevronRight, X, BookOpen, Bot, User, FolderOpen, Settings } from 'lucide-react';
import { CaldeiraIcon } from '@/components/icons/CaldeiraIcon';
import { useNavigation } from '@/contexts/NavigationContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAgents } from '@/hooks/useAgents';
import { PromptSelectorModal } from '@/components/modals/PromptSelectorModal';
import { AgentSelectorModal } from '@/components/modals/AgentSelectorModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const Breadcrumb = () => {
  const {
    navigation,
    navigateToGlobal,
    activeAgentId,
    setActiveAgentId,
    activeConversationId,
    setActiveConversationId,
    conversationHasMessages,
    setEditingResource,
  } = useNavigation();
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [removeAgentDialogOpen, setRemoveAgentDialogOpen] = useState(false);

  const { data: project } = useQuery({
    queryKey: ['project', navigation.projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, icon')
        .eq('id', navigation.projectId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!navigation.projectId,
  });

  const { data: agents = [] } = useAgents(
    navigation.isPersonal ? 'personal' : 'global',
    undefined,
    navigation.projectId,
  );
  const activeAgent = activeAgentId ? agents.find(a => a.id === activeAgentId) : undefined;

  const handleRemoveAgent = () => {
    if (activeConversationId && conversationHasMessages) {
      setRemoveAgentDialogOpen(true);
    } else {
      setActiveAgentId(null);
    }
  };

  const confirmRemoveAgent = () => {
    setActiveConversationId(null);
    setActiveAgentId(null);
    setRemoveAgentDialogOpen(false);
  };

  const agentBadge = activeAgent && (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
      Agente: {activeAgent.name}
      <button
        onClick={handleRemoveAgent}
        className="hover:bg-brand-primary/20 rounded-full p-0.5 transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );

  const actionButtons = (
    <div className="hidden md:flex items-center gap-1">
      <button
        onClick={() => setPromptModalOpen(true)}
        className="px-1.5 lg:px-3 py-1.5 rounded-md text-xs font-medium border border-color text-secondary hover:bg-hover transition-all flex items-center gap-1.5"
        title="Prompts"
      >
        <BookOpen className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="hidden lg:inline">Prompts</span>
      </button>
      <button
        onClick={() => setAgentModalOpen(true)}
        className="px-1.5 lg:px-3 py-1.5 rounded-md text-xs font-medium border border-color text-secondary hover:bg-hover transition-all flex items-center gap-1.5"
        title="Agentes"
      >
        <Bot className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="hidden lg:inline">Agentes</span>
      </button>
    </div>
  );

  const handleSelectPrompt = (prompt: { id: string; name: string; content: string }) => {
    window.dispatchEvent(new CustomEvent('inject-prompt', { detail: prompt.content }));
  };

  const handleSelectAgent = (agent: { id: string; name: string }) => {
    setActiveAgentId(agent.id);
  };

  const modals = (
    <>
      <PromptSelectorModal
        open={promptModalOpen}
        onOpenChange={setPromptModalOpen}
        onSelect={handleSelectPrompt}
        activeConversationId={activeConversationId}
      />
      <AgentSelectorModal
        open={agentModalOpen}
        onOpenChange={setAgentModalOpen}
        onSelect={handleSelectAgent}
      />
      <AlertDialog open={removeAgentDialogOpen} onOpenChange={setRemoveAgentDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Iniciar nova conversa?</AlertDialogTitle>
            <AlertDialogDescription>
              A substituição de agentes requer o início de uma nova conversa. A conversa atual será preservada no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveAgent}>Nova conversa</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  const envLabel = (() => {
    if (navigation.isPersonal) {
      return (
        <span className="text-primary font-medium flex items-center gap-1.5 text-sm">
          <User className="w-4 h-4" /> Pessoal
        </span>
      );
    }
    if (!navigation.projectId) {
      return (
        <span className="text-primary font-medium flex items-center gap-1.5 text-sm">
          <CaldeiraIcon className="w-4 h-4" /> Espaço Caldeira
        </span>
      );
    }
    const projectLabel = project ? project.name : '…';
    return (
      <div className="flex items-center gap-1.5 text-sm">
        <button
          onClick={navigateToGlobal}
          className="text-tertiary hover:text-primary hover:underline transition-colors flex items-center gap-1"
        >
          <CaldeiraIcon className="w-3.5 h-3.5" />
        </button>
        <ChevronRight className="w-3.5 h-3.5 text-tertiary" />
        <span className="text-primary font-medium flex items-center gap-1.5">
          <FolderOpen className="w-3.5 h-3.5" /> {projectLabel}
        </span>
        <button
          onClick={() => setEditingResource({ type: 'project-settings', id: navigation.projectId })}
          className="p-1 rounded-md text-tertiary hover:text-primary hover:bg-hover transition-colors"
          title="Configurações do projeto"
        >
          <Settings className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  })();

  return (
    <>
      {envLabel}
      {agentBadge}
      <div className="hidden md:contents">
        <div className="w-px h-5 bg-border mx-1" />
      </div>
      {actionButtons}
      {modals}
    </>
  );
};
